import type { Observable } from 'rxjs';
import { combineLatest, EMPTY, merge, mergeWith, NEVER, of } from 'rxjs';
import { catchError, debounceTime, delay, map, switchMap, tap } from 'rxjs/operators';

import type { AdminsPresencesMap } from '@recruitee/account-data';
import { syncPresence } from '@recruitee/account-data';
import type { User } from '@recruitee/account-types';
import { uniqByKey } from '@recruitee/utils';
import type { AdminSocket, NotesChannel, NotesChannelName } from '@recruitee/websocket';
import { bufferUntil, fromChannel } from '@recruitee/websocket';

import type { CurrentlyTypedNotes } from './notes.state';

export const notesChannel$ = (socket: AdminSocket, channelName: NotesChannelName) =>
  socket.ready$.pipe(
    switchMap(() => socket.subscribeToChannel(channelName)),
    catchError(err => {
      console.error(err);
      return NEVER;
    }),
  );

export const notesWebsockets$ = (
  channel$: Observable<NotesChannel>,
  pageVisibleTrigger: () => Observable<any>,
  onPresencesUpdated: (presences: AdminsPresencesMap) => void,
  onNotesUpdated: () => void,
) =>
  channel$.pipe(
    switchMap(channel =>
      merge(
        syncPresence(channel).pipe(
          bufferUntil(pageVisibleTrigger),
          tap(presences => onPresencesUpdated(presences[presences.length - 1])),
        ),
        fromChannel(channel, 'notes-updated').pipe(tap(() => onNotesUpdated())),
      ),
    ),
  );

// eslint-disable-next-line max-lines-per-function
export const currentlyTypedNotes$ = (
  admins$: Observable<User[]>,
  adminsPresencesMap$: Observable<AdminsPresencesMap>,
  currentAdminId: number,
) =>
  adminPresences$(admins$, adminsPresencesMap$, currentAdminId).pipe(
    map(adminPresences =>
      adminPresences
        .filter(p => p.currentlyTypedNotes.length > 0)
        .map(p =>
          p.currentlyTypedNotes.map(note => ({
            admin: {
              ...p.admin,
              lastOnlineAt: p.lastOnlineAt,
            },
            replyToId: note?.reply_to_id,
          })),
        )
        .flat(),
    ),
    switchMap(notes => {
      const currentTime = new Date().getTime();
      return notes.length > 0
        ? combineLatest(
            notes.map(note => {
              const showIn = note.admin.lastOnlineAt + 300 - currentTime;
              const hideIn = note.admin.lastOnlineAt + 6300 - currentTime;
              if (hideIn < 0) {
                return EMPTY;
              } else if (showIn < 0) {
                return of(note).pipe(mergeWith(of(undefined).pipe(delay(hideIn))));
              } else {
                return of(note).pipe(delay(showIn), mergeWith(of(undefined).pipe(delay(hideIn))));
              }
            }),
          ).pipe(debounceTime(50))
        : of([]);
    }),
    map(notes => <CurrentlyTypedNotes>(<unknown>notes.filter(Boolean))),
  );

const adminPresences$ = (
  admins$: Observable<User[]>,
  adminsPresencesMap$: Observable<AdminsPresencesMap>,
  currentAdminId: number,
) =>
  combineLatest([admins$, adminsPresencesMap$]).pipe(
    map(([admins, adminsPresencesMap]) =>
      Object.keys(adminsPresencesMap)
        .filter(adminId => +adminId !== currentAdminId)
        .map(adminId => {
          const metas = adminsPresencesMap[adminId].metas;
          return {
            lastOnlineAt: Math.max(...metas.map(m => m.online_at)),
            admin: {
              id: +adminId,
              ...(admins!.find(admin => admin.id === +adminId) || {}),
              isOnline: true,
            },
            currentlyTypedNotes: uniqByKey(
              metas.filter(m => m.typed_note).map(m => m.typed_note!),
              'reply_to_id',
            ),
          };
        })
        .sort((a, b) => b.lastOnlineAt - a.lastOnlineAt),
    ),
  );
